import type { CoverageBlock } from "@/src/models/intake/blocks/block";
import type { EnrichedContentIndexable } from "@/src/models/intake/blocks/enriched-content";
import {
  getEligibilityCoveragePosition,
  storedStediEligibilityCheckSchema,
  type EligibilityCoverage,
  type StediEligibilityCheck,
  type VerifyInsuranceEligibilityResponse,
  verifyInsuranceEligibilityResponseSchema,
} from "@/src/models/stedi-eligibility-check";
import { fetchJSON } from "@/src/requests/utils/fetch-json";

async function getEligibilityCheck(
  path: string,
  coverage: EligibilityCoverage | null
): Promise<StediEligibilityCheck | null> {
  if (coverage === null) return null;

  const position = getEligibilityCoveragePosition(coverage);

  return storedStediEligibilityCheckSchema.nullable().parse(await fetchJSON(`${path}?coveragePosition=${position}`));
}

export function getEligibilityCheckByReferralId(referralId: string, coverage: EligibilityCoverage | null) {
  return getEligibilityCheck(`/api/referrals/${referralId}/eligibility`, coverage);
}

export function getEligibilityCheckByTaskId(taskId: string, coverage: EligibilityCoverage | null) {
  return getEligibilityCheck(`/api/tasks/${taskId}/eligibility`, coverage);
}

export function getEligibilityCheckByOrderId(orderId: string, coverage: EligibilityCoverage | null) {
  return getEligibilityCheck(`/api/orders/${orderId}/eligibility`, coverage);
}

export type VerifyInsuranceEligibilityRequestPayload = {
  enrichedContent: EnrichedContentIndexable;
  coverage: CoverageBlock["reviewedFields"];
  force?: boolean;
};

export async function verifyInsuranceEligibilityAtIntake(
  taskId: string,
  payload: VerifyInsuranceEligibilityRequestPayload
): Promise<VerifyInsuranceEligibilityResponse> {
  return verifyInsuranceEligibilityResponseSchema.parse(
    await fetchJSON(`/api/tasks/${taskId}/verify-insurance-eligibility`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
  );
}

function selectedCoverageId(coverage: EligibilityCoverage | null): string {
  if (!coverage?.id) throw new Error("A saved insurance policy is required to recheck eligibility");

  return coverage.id;
}

export async function recheckOrderEligibility(
  orderId: string,
  coverage: EligibilityCoverage | null,
  force = true
): Promise<VerifyInsuranceEligibilityResponse> {
  return verifyInsuranceEligibilityResponseSchema.parse(
    await fetchJSON(`/api/orders/${orderId}/eligibility`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ force, coverageId: selectedCoverageId(coverage) }),
    })
  );
}

export async function recheckTaskEligibility(
  taskId: string,
  coverage: EligibilityCoverage | null
): Promise<VerifyInsuranceEligibilityResponse> {
  const coverageId = selectedCoverageId(coverage);

  return verifyInsuranceEligibilityResponseSchema.parse(
    await fetchJSON(`/api/tasks/${taskId}/eligibility?coverageId=${coverageId}`, { method: "POST" })
  );
}
